import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Filter } from "lucide-react";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecentWorkPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter((p) => p.category === activeFilter));
    }
  }, [activeFilter, projects]);

  const filters = [
    { key: "all", label: "All Work" },
    { key: "windows", label: "Windows" },
    { key: "doors", label: "Doors" },
    { key: "outbuilds", label: "Outbuilds" },
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case "windows":
        return "bg-blue-500";
      case "doors":
        return "bg-green-500";
      case "outbuilds":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div data-testid="recent-work-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Recent Work | Double Glazing Near Me | Window Fitters Lanarkshire"
        description="View recent double glazing installations by local double glazing installers in Lanarkshire. High-quality uPVC windows, composite doors & affordable replacement windows in Motherwell, Hamilton, Glasgow. Call 07917 181597"
        keywords={`double glazing near me, window fitters near me, door installers near me, local window companies, local double glazing installers Lanarkshire, high-quality uPVC windows, affordable replacement windows, double glazing gallery, recent installations, window installations Motherwell, door installations Hamilton, double glazing work Glasgow, double glazing work Lanarkshire, ${LOCATION_KEYWORDS}`}
        canonical="/recent-work"
      />
      {/* Hero Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            RECENT <span className="text-[#FF6B00]">WORK</span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Looking for double glazing near me? Browse our gallery of recent work completed by our local double glazing installers in Lanarkshire. From high-quality <Link to="/windows" className="text-[#FF6B00] hover:underline">uPVC windows</Link> to <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link> and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>, see the quality of our workmanship across Glasgow, Hamilton, Motherwell, and surrounding areas.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-[#F5F5F5] border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-[#525252]" />
            {filters.map((filter) => (
              <button
                key={filter.key}
                data-testid={`filter-${filter.key}`}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 font-bold uppercase text-sm tracking-wide transition-all ${
                  activeFilter === filter.key
                    ? "bg-[#FF6B00] text-white"
                    : "bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4" />
              <p className="text-[#525252]">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#525252]">No projects found in this category.</p>
            </div>
          ) : (
            <div className="masonry-grid">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  data-testid={`project-card-${index}`}
                  className="group bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#FF6B00] transition-all"
                >
                  <div className="aspect-[3/4] overflow-hidden relative">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div
                      className={`absolute top-4 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-wide ${getCategoryColor(
                        project.category
                      )}`}
                    >
                      {project.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3
                      className="text-lg font-black text-[#0A0A0A] mb-2"
                      style={{ fontFamily: "'Chivo', sans-serif" }}
                    >
                      {project.title}
                    </h3>
                    <p className="text-[#525252] text-sm mb-3">
                      {project.description}
                    </p>
                    {project.location && (
                      <p className="text-xs text-[#FF6B00] font-bold uppercase tracking-wide">
                        {project.location}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F5F5F5] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl font-black text-[#0A0A0A] mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WANT SIMILAR RESULTS?
          </h2>
          <p className="text-[#525252] mb-8 max-w-2xl mx-auto">
            As local double glazing installers in Lanarkshire, we'd love to help transform your home with our high-quality uPVC <Link to="/windows" className="text-[#FF6B00] hover:underline">windows</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">doors</Link>, and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>. Get in touch today for a free, no-obligation quote on affordable replacement windows and doors.
          </p>
          <Link
            to="/contact"
            data-testid="recent-work-cta"
            className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all"
          >
            Get Free Quote <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            YOUR LOCAL <span className="text-[#FF6B00]">WINDOW FITTERS</span> NEAR ME
          </h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            Searching for window fitters near me or door installers near me in Lanarkshire? C. Glancey Double Glazing is one of the most trusted local window companies in the area. As experienced local double glazing installers in Lanarkshire, we have built our reputation on quality workmanship, honest pricing, and outstanding customer service.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6">
            Every project in our gallery showcases the high-quality uPVC windows and composite doors we install across <Link to="/double-glazing-hamilton" className="text-[#FF6B00] hover:underline">Hamilton</Link>, <Link to="/double-glazing-motherwell" className="text-[#FF6B00] hover:underline">Motherwell</Link>, <Link to="/double-glazing-east-kilbride" className="text-[#FF6B00] hover:underline">East Kilbride</Link>, <Link to="/double-glazing-airdrie" className="text-[#FF6B00] hover:underline">Airdrie</Link>, <Link to="/double-glazing-coatbridge" className="text-[#FF6B00] hover:underline">Coatbridge</Link>, <Link to="/double-glazing-wishaw" className="text-[#FF6B00] hover:underline">Wishaw</Link>, Glasgow, and across the whole of Lanarkshire. We use only premium Rehau and Veka profiles — the same systems trusted by leading window fitters throughout the UK.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6">
            Whether you need affordable replacement windows to upgrade your home's energy efficiency, new composite doors for improved security, or a complete house of double glazing near me, our team delivers professional results every time. All our installations come with a 10 year guarantee and are carried out by our own trained door installers and window fitters — we never use subcontractors.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Don't just take our word for it — read our <Link to="/reviews" className="text-[#FF6B00] hover:underline">customer reviews</Link> to see why homeowners across Lanarkshire choose us as their local window company. If you're looking for double glazing near me, <Link to="/contact" className="text-[#FF6B00] hover:underline">get in touch</Link> today for a free, no-obligation quote.
          </p>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Explore Our Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite & uPVC Doors
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Customer Reviews
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Window Installation FAQ
            </Link>
            <Link to="/contact" className="px-6 py-3 bg-[#F5F5F5] border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecentWorkPage;
